import { isAxiosError } from 'axios';
import { atomWithMutation, atomWithQuery } from 'jotai-tanstack-query';
import { fetchEventSource } from '@microsoft/fetch-event-source';

import { instance } from '@/interfaces';
import { selectedReportAtom } from '@/components/fda/ElectronicSignature';

import type { Getter } from 'jotai';
import type { APIResponse } from '@/@types/types';
import type { AssignEndorsementAtomData, EndorserData, ReportUrlData } from '@/@types/report';
import type { ReportListData } from '@/@types/site';

export async function getReportUrl(reportId: string) {
  try {
    const { data } = await instance.get<APIResponse<ReportUrlData>>(`/report/${reportId}/download-url`);
    return data.data.url;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getReportUrl] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getReportUrl] Axios error message:', err.message);
      }
    } else {
      console.error('[getReportUrl] Unknown error', err);
    }
    return '';
  }
}

export const deleteReportAtom = atomWithMutation(() => ({
  mutationFn: async (reportId: string) => {
    const isSuccess = await deleteReport(reportId);
    return isSuccess;
  },
}));
async function deleteReport(reportId: string) {
  try {
    const { data } = await instance.delete<APIResponse>(`/report/${reportId}`);
    if (data.errorCode !== 'OK') {
      console.error('[deleteReport] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[deleteReport] API error code:', err.response.data.errorCode);
      } else {
        console.error('[deleteReport] Axios error message:', err.message);
      }
    } else {
      console.error('[deleteReport] Unknown error', err);
    }
    return false;
  }
}

export const endorsersAtom = atomWithQuery((get: Getter) => ({
  queryKey: ['REPORTS', get(selectedReportAtom)],
  queryFn: async ({ queryKey: [, selectedReportAtomValue] }) => {
    const report = selectedReportAtomValue as ReportListData;
    const data = await getEndorsers(report.id);

    return data;
  },
}));
async function getEndorsers(reportId: string) {
  try {
    const { data } = await instance.get<APIResponse<EndorserData[]>>(`/report/${reportId}/endorsement`);
    return data.data;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[getEndorsers] API error code:', err.response.data.errorCode);
      } else {
        console.error('[getEndorsers] Axios error message:', err.message);
      }
    } else {
      console.error('[getEndorsers] Unknown error', err);
    }
    return [];
  }
}

export const assignEndorsementAtom = atomWithMutation(() => ({
  mutationFn: async (data: AssignEndorsementAtomData) => {
    const isSuccess = await assignEndorsement(data);
    return isSuccess;
  },
}));
async function assignEndorsement(data: AssignEndorsementAtomData) {
  const { reportId, userId, endorsementId } = data;

  try {
    const { data } = await instance.post<APIResponse>(`/report/${reportId}/endorsement`, { userId, endorsementId });
    if (data.errorCode !== 'OK') {
      console.error('[assignEndorsement] API error code:', data.errorCode);
      return false;
    }
    return true;
  } catch (err) {
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[assignEndorsement] API error code:', err.response.data.errorCode);
      } else {
        console.error('[assignEndorsement] Axios error message:', err.message);
      }
    } else {
      console.error('[assignEndorsement] Unknown error', err);
    }
    return false;
  }
}

type SignReportAtomData = { reportId: string; userId: string; password: string };
export const signReportAtom = atomWithMutation(() => ({
  mutationFn: async (data: SignReportAtomData) => {
    const res = await signReport(data);
    return res;
  },
}));
async function signReport(data: SignReportAtomData) {
  const { reportId, userId, password } = data;

  try {
    const { data } = await instance.post<APIResponse>(`/report/${reportId}/electronic-signature`, { userId, password });
    if (data.errorCode !== 'OK') {
      console.error('[signReport] API error code:', data.errorCode);
      return data.errorCode;
    }
    return 'OK';
  } catch (err) {
    let errorCode: string;
    if (isAxiosError(err)) {
      if (err.response) {
        console.error('[signReport] API error code:', err.response.data.errorCode);
        errorCode = err.response.data.errorCode;
      } else {
        console.error('[signReport] Axios error message:', err.message);
        errorCode = 'AXIOS_ERROR';
      }
    } else {
      console.error('[signReport] Unknown error', err);
      errorCode = 'UNKNOWN';
    }
    return errorCode;
  }
}

export function subscribeReportGenerate(jobId: string, onComplete: () => void, onError: () => void) {
  const controller = new AbortController();
  const token = JSON.parse(localStorage.getItem('TOKEN')).accessToken;

  fetchEventSource(`${process.env.REACT_APP_API_URL}/report/job/${jobId}`, {
    headers: { Authorization: `Bearer ${token}` },
    signal: controller.signal,
    onmessage: ({ event }) => {
      if (event === 'completed') {
        controller.abort();
        onComplete();
      }
      if (event === 'error') {
        controller.abort();
        onError();
      }
    },
  });
}
